import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { customThemePreset } from '../custom-theme-preset';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { errorInterceptor } from './common/interceptors/error.interceptor';
import { loaderInterceptor } from './common/interceptors/loader.interceptor';
import { mockInterceptor } from './common/interceptors/mock.interceptor';
import { InsightsService } from './common/services/insights.service';
import { MockAuthService } from './common/services/mock-auth.service';
import { MockBroadcastService } from './common/services/mock-broadcast.service';

export function loggerCallback(logLevel: LogLevel, message: string) {
  //console.log(logLevel, message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/logged-out',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.graphUri,
    environment.apiConfig.graphScopes
  );
  protectedResourceMap.set(
    environment.apiConfig.apiEndpointBase,
    [environment.apiConfig.apiScope]
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.graphScopes, environment.apiConfig.apiScope],
    },
    loginFailedRoute: '/not-authorized',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(
      BrowserModule
    ),
    provideNoopAnimations(),

    // HTTP Client with conditional interceptors
    provideHttpClient(
      withInterceptors([
        // Add mock interceptor first if mockMode is enabled
        ...(environment.mockMode ? [mockInterceptor] : []),
        loaderInterceptor,
        errorInterceptor
      ]),
      withInterceptorsFromDi(),
      withFetch()
    ),

    // Conditionally provide MSAL services only when auth is NOT disabled
    ...(environment.disableAuth ? [] : [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
      },
      {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
      },
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
    ]),

    // Provide Mock Auth Service when auth is disabled
    ...(environment.disableAuth ? [
      {
        provide: MsalService,
        useClass: MockAuthService,
      },
      {
        provide: MsalGuard,
        useValue: { canActivate: () => true }, // Mock guard that always allows access
      },
      {
        provide: MsalBroadcastService,
        useClass: MockBroadcastService,
      },
    ] : []),

    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: customThemePreset,
        options: {
          darkModeSelector: '.qh-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      },
    }),
    InsightsService,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    MessageService
  ],
};
